import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useNav } from '../lib/nav'
import * as Sentry from '@sentry/react'
import { Page, TopBar } from '../components/Shell'
import { InputRow, KV } from '../components/Controls'
import { I } from '../components/Icons'
import { Skeleton, Empty } from '../components/Skeleton'
import { useStore } from '../state/store'
import { createCustomer, updateCustomer, searchShopifyCustomers, linkShopifyCustomer, type ShopifyHit } from '../lib/api'
import { customerContact, customerName } from '../lib/types'
import type { Customer } from '../lib/types'
import { Working } from '../components/StarTick'
import { ContactButtons } from '../components/Contact'
import { useToast } from '../components/Toast'

const blank = { first_name: '', last_name: '', phone: '', email: '', instagram: '', notes: '' }

/** People Starchive buys from (and, from Phase 2, sells to). Mirrored to Shopify customers. */
export function Customers() {
  const toast = useToast()
  const nav = useNav()
  const [sp] = useSearchParams()
  const pickMode = sp.get('pick')
  const editId = sp.get('edit')
  const picking = pickMode === '1' || pickMode === 'sale' || pickMode === 'trade'
  const { customers, setDraft, setSale, setTrade, refresh, ready } = useStore()
  const [q, setQ] = useState('')
  const editing0 = editId ? customers.find((c) => c.id === editId) : undefined
  const [sel, setSel] = useState<string>(editing0 ? editing0.id : picking ? '' : (customers[0]?.id ?? ''))
  const [mode, setMode] = useState<'detail' | 'new' | 'edit'>(editing0 ? 'edit' : picking && customers.length === 0 ? 'new' : 'detail')
  const [nc, setNc] = useState(editing0 ? { first_name: editing0.first_name, last_name: editing0.last_name, phone: editing0.phone ?? '', email: editing0.email ?? '', instagram: editing0.instagram ?? '', notes: editing0.notes ?? '' } : { ...blank })
  const [hits, setHits] = useState<ShopifyHit[]>([])
  const [searching, setSearching] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  const list = customers.filter((c) => !q || `${c.first_name} ${c.last_name} ${c.phone} ${c.email} ${c.instagram ?? ''}`.toLowerCase().includes(q.toLowerCase()))
  const cur = customers.find((c) => c.id === sel)
  // Shopify is the customer list of record: anything typed is also looked up there, and Shopify-only people are offered below the local matches.
  useEffect(() => {
    const term = q.trim()
    if (term.length < 2) { setHits([]); return }
    let live = true
    setSearching(true)
    const t = setTimeout(() => {
      searchShopifyCustomers(term).then((r) => { if (live) setHits(r.shopify.filter((h) => !h.local_id)) }).catch(() => { if (live) setHits([]) }).finally(() => { if (live) setSearching(false) })
    }, 350)
    return () => { live = false; clearTimeout(t) }
  }, [q])
  const pickHit = async (h: ShopifyHit) => {
    setBusy(true); setErr('')
    try {
      const c = await linkShopifyCustomer(h.shopify_customer_id, null)
      await refresh(); setQ('')
      if (picking) use(c); else { setSel(c.id); setMode('detail') }
    } catch (e) { Sentry.captureException(e); setErr((e as Error).message) } finally { setBusy(false) }
  }
  const use = (c: Customer) => {
    if (pickMode === 'trade') { setTrade({ customerId: c.id, customerName: customerName(c) }); nav('/trade'); return }
    if (pickMode === 'sale') { setSale({ customerId: c.id, customerName: customerName(c) }); nav('/sell/pay') }
    else { setDraft({ customerId: c.id, from: customerName(c) }); nav('/intake') }
  }
  const pick = (c: Customer) => { if (picking) use(c); else { setSel(c.id); setMode('detail') } }
  const ok = nc.first_name.trim() && (nc.phone.trim() || nc.email.trim() || nc.instagram.trim()) && (!nc.email.trim() || /\S+@\S+\.\S+/.test(nc.email.trim()))
  const save = async () => {
    setBusy(true); setErr('')
    try {
      if (mode === 'edit' && cur) {
        const c = await updateCustomer({ id: cur.id, ...nc }, null)
        await refresh(); setNc({ ...blank }); setMode('detail')
        if (editId && pickMode === 'trade') { setTrade({ customerId: c.id, customerName: customerName(c) }); nav('/trade'); return }
        if (editId && pickMode === 'sale') { setSale({ customerId: c.id, customerName: customerName(c) }); nav('/sell/pay'); return }
        if (c.shopify_error) setErr('Saved here; Shopify said: ' + c.shopify_error)
        return
      }
      const c = await createCustomer(nc, null)
      await refresh(); setNc({ ...blank })
      if (picking) use(c); else { setSel(c.id); setMode('detail'); if (c.shopify_error) setErr('Saved here, but not in Shopify: ' + c.shopify_error) }
    } catch (e) { Sentry.captureException(e); setErr((e as Error).message) } finally { setBusy(false) }
  }
  const startEdit = () => { if (!cur) return; setNc({ first_name: cur.first_name, last_name: cur.last_name, phone: cur.phone ?? '', email: cur.email ?? '', instagram: cur.instagram ?? '', notes: cur.notes ?? '' }); setErr(''); setMode('edit') }

  return (
    <Page
      bar={picking ? <TopBar back={pickMode === 'sale' ? 'Payment' : pickMode === 'trade' ? 'Trade' : 'Intake'} backTo={pickMode === 'sale' ? '/sell/pay' : pickMode === 'trade' ? '/trade' : '/intake'} title={pickMode === 'sale' || pickMode === 'trade' ? 'Customer' : 'Who is it from?'} /> : <TopBar back="Home" backTo="/" title="Customers" caption={`${customers.length} people`} />}
      aside={
        <>
          <div className="aside__body">
            {mode === 'new' || mode === 'edit' ? (
              <>
                <div className="section-title" style={{ padding: '16px 20px 4px' }}>{mode === 'edit' ? 'Edit customer' : 'New customer'}</div>
                <div className="card" style={{ margin: '8px 20px' }}>
                  <InputRow id="cuf" label="First name" value={nc.first_name} onChange={(v) => setNc({ ...nc, first_name: v })} placeholder="First" />
                  <InputRow id="cul" label="Last name" value={nc.last_name} onChange={(v) => setNc({ ...nc, last_name: v })} placeholder="Last" />
                  <InputRow id="cup" label="Phone" value={nc.phone} onChange={(v) => setNc({ ...nc, phone: v })} placeholder="Phone number" tel />
                  <InputRow id="cui" label="Instagram" value={nc.instagram} onChange={(v) => setNc({ ...nc, instagram: v })} placeholder="@handle" left />
                  <InputRow id="cue" label="Email" value={nc.email} onChange={(v) => setNc({ ...nc, email: v })} placeholder="Email" email />
                  <div className="hint" style={{ padding: '6px 16px 10px' }}>One way to reach them is enough: phone, Instagram or email.</div>
                  <InputRow id="cun" label="Note" value={nc.notes} onChange={(v) => setNc({ ...nc, notes: v })} placeholder="Optional" />
                </div>
                <div className="hint" style={{ padding: '10px 24px' }}>{mode === 'edit' ? 'Changes go to Starchive\'s records and to their Shopify profile.' : 'Saved to Starchive\'s records and added to Shopify as a customer, so buying and selling history sits in one place.'}</div>
                {err && <div className="banner" style={{ margin: '0 20px', background: '#d70015' }}>{err}</div>}
              </>
            ) : cur ? (
              <>
                <div className="detail-head"><span className="person__initial">{cur.first_name[0] ?? '?'}</span><b>{customerName(cur)}</b></div>
                {(cur.phone || cur.email || cur.instagram) && <ContactButtons name={customerName(cur)} phone={cur.phone ?? ''} email={cur.email ?? ''} instagram={cur.instagram ?? ''} pref={cur.phone ? 'text' : cur.instagram ? 'instagram' : 'email'} toast={toast} subject="Starchive" body={`Hi ${cur.first_name}, this is Starchive. `} />}
                <div className="card review-rows" style={{ margin: '8px 20px' }}>
                  <KV k="Phone" v={cur.phone || '—'} />
                  <KV k="Instagram" v={cur.instagram || '—'} />
                  <KV k="Email" v={cur.email || '—'} />
                  <KV k="Note" v={cur.notes || '—'} />
                  <KV k="Pieces bought from them" v={String(cur.pieces ?? 0)} />
                  <KV k="Shopify customer" v={cur.in_shopify ? 'Linked' : 'Not yet (retrying)'} />
                </div>
                {err && <div className="banner" style={{ margin: '8px 20px', background: '#d70015' }}>{err}</div>}
              </>
            ) : <div className="empty">Pick a person to see their details.</div>}
          </div>
          <div className="aside__foot">
            {mode === 'edit'
              ? <div style={{ display: 'flex', gap: 10 }}><button type="button" className="cta cta--center" style={{ flex: 1, background: '#e5e5ea', color: '#000' }} disabled={busy} onClick={() => { if (editId && pickMode === 'sale') { nav('/sell/pay'); return } if (editId && pickMode === 'trade') { nav('/trade'); return } setMode('detail'); setNc({ ...blank }) }}>Cancel</button><button type="button" className="cta cta--center" style={{ flex: 2 }} disabled={!ok || busy} onClick={save}>{busy ? <Working>Saving</Working> : 'Save changes'}</button></div>
              : mode === 'new'
              ? <button type="button" className="cta cta--center" disabled={!ok || busy} onClick={save}>{busy ? <Working>Adding</Working> : picking ? (pickMode === 'sale' ? 'Add and use for this sale' : pickMode === 'trade' ? 'Add and use for this trade' : 'Add and use for this intake') : 'Add customer'}</button>
              : <div style={{ display: 'flex', gap: 10 }}>{cur && !picking && <button type="button" className="cta cta--center" style={{ flex: 1, background: '#e5e5ea', color: '#000' }} onClick={startEdit}>Edit</button>}<button type="button" className="cta cta--center" style={{ flex: 2 }} onClick={() => { setNc({ ...blank }); setMode('new') }}>New customer</button></div>}
          </div>
        </>
      }
    >
      {picking && <div className="banner">{pickMode === 'sale' || pickMode === 'trade' ? 'Pick the customer, or add them.' : 'Pick who this piece was bought from, or add them.'}</div>}
      <div className="search">{I.search()}<label htmlFor="cq" style={{ position: 'absolute', left: -9999 }}>Search customers</label><input id="cq" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, phone or email" /></div>
      <div className="card">
        {list.map((c) => (
          <button key={c.id} type="button" className={'person' + (c.id === sel && !picking ? ' sel' : '')} onClick={() => pick(c)}>
            <span className="person__initial">{c.first_name[0] ?? '?'}</span>
            <span style={{ flexGrow: 1, minWidth: 0 }}><span className="person__name">{customerName(c)}</span><span className="person__sub">{customerContact(c) || 'No contact details'} · {c.pieces ?? 0} {c.pieces === 1 ? 'piece' : 'pieces'}</span></span>
            {I.chev()}
          </button>
        ))}
        {hits.map((h) => (
          <button key={h.shopify_customer_id} type="button" className="person" disabled={busy} onClick={() => pickHit(h)}>
            <span className="person__initial" style={{ background: '#5856d6' }}>{h.first_name[0] ?? '?'}</span>
            <span style={{ flexGrow: 1, minWidth: 0 }}><span className="person__name">{`${h.first_name} ${h.last_name}`.trim() || h.email || 'Unnamed'}</span><span className="person__sub">{[h.phone, h.email].filter(Boolean).join(' · ') || 'No contact details'} · in Shopify</span></span>
            {I.chev()}
          </button>
        ))}
        {!ready && list.length === 0 && hits.length === 0 && <Skeleton rows={6} h={64} card={false} />}
        {ready && list.length === 0 && hits.length === 0 && (searching ? <div className="empty">Checking Shopify…</div> : <Empty icon={I.cons(24)} line={q.trim().length >= 2 ? 'No one matches here or in Shopify.' : 'No one matches.'} action="Add as new" onAction={() => setMode('new')} />)}
      </div>
    </Page>
  )
}
